/**
 * XRMeshSystem — tracks real-world geometry from the mesh-detection feature
 * (Quest 3/3S scene mesh: the room's actual triangulated surfaces).
 *
 * Mirrors XRPlaneSystem: `frame.detectedMeshes` diffs into stable-id entries
 * on XRSessionState.detectedMeshes with add/update/remove events. Vertex and
 * index buffers are COPIED only when `lastChangedTime` moves — the UA may
 * reuse its buffers across frames, and consumers hold ours across frames.
 *
 * Phase: RenderSub, SubPhase: PreUpdate, Priority: 12
 */

import type { ISystem } from '../../ecs/System.js';
import type { World } from '../../ecs/World.js';
import type { EventBus } from '../../core/EventBus.js';
import type { XRSessionState, XRMeshInfo } from '../types.js';
import { LoopPhase, SubPhase } from '../../shared/types.js';
import { Logger } from '../../core/Logger.js';

export class XRMeshSystem implements ISystem {
  readonly name = 'XRMesh';
  readonly phase = LoopPhase.RenderSub;
  readonly subPhase = SubPhase.PreUpdate;
  readonly priority = 12;

  private _state: XRSessionState;
  private _events: EventBus;
  private _meshSession: XRSession | null = null;
  private _warnedMissingFrame = false;
  private readonly _current = new Set<XRMesh>();

  constructor(state: XRSessionState, events: EventBus) {
    this._state = state;
    this._events = events;
  }

  execute(_dt: number, _world: World): void {
    if (!this._state.isPresenting) return;
    if (!this._state.hasFeature('mesh-detection')) return;

    const session = this._state.session!;
    if (session !== this._meshSession) {
      this._meshSession = session;
      this._state.meshIdMap.clear();
      this._state.detectedMeshes.clear();
    }

    const frame = (globalThis as { __xrFrame?: XRFrame }).__xrFrame;
    if (!frame) {
      if (!this._warnedMissingFrame) {
        Logger.warn('XRMesh', '__xrFrame missing during active session — App animation loop not populating globalThis.__xrFrame');
        this._warnedMissingFrame = true;
      }
      return;
    }

    const detected = frame.detectedMeshes;
    const refSpace = this._state.referenceSpace;
    if (!detected || !refSpace) return;

    this._current.clear();
    for (const mesh of detected) {
      this._current.add(mesh);

      let id = this._state.meshIdMap.get(mesh);
      const isNew = id === undefined;
      if (id === undefined) {
        id = this._state.nextMeshId();
        this._state.meshIdMap.set(mesh, id);
      }

      const existing = this._state.detectedMeshes.get(id);
      if (existing && existing.lastChangedTime === mesh.lastChangedTime) {
        const pose = frame.getPose(mesh.meshSpace, refSpace);
        if (pose) existing.matrix.set(pose.transform.matrix);
        continue;
      }

      const pose = frame.getPose(mesh.meshSpace, refSpace);
      if (!pose) continue;

      const info: XRMeshInfo = existing ?? {
        id,
        matrix: new Float32Array(16),
        vertices: new Float32Array(0),
        indices: new Uint32Array(0),
        semanticLabel: mesh.semanticLabel ?? '',
        lastChangedTime: mesh.lastChangedTime,
      };
      info.matrix.set(pose.transform.matrix);
      // Copy: UA-owned buffers may be recycled after this frame.
      info.vertices = new Float32Array(mesh.vertices);
      info.indices = new Uint32Array(mesh.indices);
      info.semanticLabel = mesh.semanticLabel ?? '';
      info.lastChangedTime = mesh.lastChangedTime;
      this._state.detectedMeshes.set(id, info);

      this._events.emit(isNew ? 'xr:mesh-added' : 'xr:mesh-updated', {
        meshId: id,
        vertexCount: info.vertices.length / 3,
        semanticLabel: info.semanticLabel,
      });
    }

    for (const [mesh, id] of this._state.meshIdMap) {
      if (this._current.has(mesh)) continue;
      this._state.meshIdMap.delete(mesh);
      this._state.detectedMeshes.delete(id);
      this._events.emit('xr:mesh-removed', { meshId: id });
    }
  }
}
